// UserProfileEducation.tsx
import React from "react";
import { TeamMember } from "../../../types/admin/TeamTypes";
import { Button, Input, Space, Typography } from "antd";
import { PlusOutlined, DeleteOutlined, BookOutlined } from "@ant-design/icons";
import { SectionCard, EmptyState } from "../../../components/shared";

const { Text } = Typography;

interface UserProfileEducationProps {
	teamMember: TeamMember;
	isEditing: boolean;
	setTeamMember: React.Dispatch<React.SetStateAction<TeamMember | null>>;
}

interface EducationEntry {
	degree: string;
	institution: string;
	year: string;
}

const UserProfileEducation: React.FC<UserProfileEducationProps> = ({ teamMember, isEditing, setTeamMember }) => {
	const educationHistory = teamMember.team_member_info?.education_history || [];

	const handleEducationChange = (index: number, field: keyof EducationEntry, value: string) => {
		setTeamMember((prev) => {
			if (!prev) return null;
			const prevEducation = prev.team_member_info?.education_history || [];
			const updatedEducation = prevEducation.map((entry: EducationEntry, idx: number) => {
				if (idx === index) {
					return { ...entry, [field]: value };
				}
				return entry;
			});
			return {
				...prev,
				team_member_info: {
					...prev.team_member_info,
					education_history: updatedEducation,
				},
			};
		});
	};

	const addEducationEntry = () => {
		setTeamMember((prev) => {
			if (!prev) return null;
			const prevEducation = prev.team_member_info?.education_history || [];
			return {
				...prev,
				team_member_info: {
					...prev.team_member_info,
					education_history: [...prevEducation, { degree: "", institution: "", year: "" }],
				},
			};
		});
	};

	const removeEducationEntry = (index: number) => {
		setTeamMember((prev) => {
			if (!prev) return null;
			const prevEducation = prev.team_member_info?.education_history || [];
			const updatedEducation = prevEducation.filter((_: any, idx: number) => idx !== index);
			return {
				...prev,
				team_member_info: {
					...prev.team_member_info,
					education_history: updatedEducation,
				},
			};
		});
	};

	if (educationHistory.length === 0 && !isEditing) {
		return (
			<EmptyState
				icon={<BookOutlined />}
				title="No education history"
				description="Click 'Edit Profile' to add your educational background"
			/>
		);
	}

	return (
		<div>
			{educationHistory.length > 0 && (
				<Space direction="vertical" size="large" style={{ width: "100%" }}>
					{educationHistory.map((entry: EducationEntry, index: number) => (
						<SectionCard
							key={index}
							title={`Education ${index + 1}`}
							extra={isEditing && <Button type="text" danger size="small" icon={<DeleteOutlined />} onClick={() => removeEducationEntry(index)} />}
						>
							<Space direction="vertical" size="middle" style={{ width: "100%" }}>
								<div>
									<Text type="secondary" style={{ fontSize: 11 }}>Degree / Qualification</Text>
									{isEditing ? (
										<Input value={entry.degree} onChange={(e) => handleEducationChange(index, "degree", e.target.value)} placeholder="e.g., Bachelor of Medicine" style={{ marginTop: 4 }} />
									) : (
										<Text style={{ fontSize: 13, display: "block", marginTop: 4 }}>{entry.degree || "—"}</Text>
									)}
								</div>

								<div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 12 }}>
									<div>
										<Text type="secondary" style={{ fontSize: 11 }}>Institution</Text>
										{isEditing ? (
											<Input value={entry.institution} onChange={(e) => handleEducationChange(index, "institution", e.target.value)} placeholder="e.g., University of Melbourne" style={{ marginTop: 4 }} />
										) : (
											<Text style={{ fontSize: 13, display: "block", marginTop: 4 }}>{entry.institution || "—"}</Text>
										)}
									</div>

									<div>
										<Text type="secondary" style={{ fontSize: 11 }}>Year</Text>
										{isEditing ? (
											<Input value={entry.year} onChange={(e) => handleEducationChange(index, "year", e.target.value)} placeholder="2020" style={{ marginTop: 4 }} />
										) : (
											<Text style={{ fontSize: 13, display: "block", marginTop: 4 }}>{entry.year || "—"}</Text>
										)}
									</div>
								</div>
							</Space>
						</SectionCard>
					))}
				</Space>
			)}

			{isEditing && (
				<Button type="default" icon={<PlusOutlined />} onClick={addEducationEntry} style={{ marginTop: educationHistory.length > 0 ? 16 : 0, width: "100%" }}>
					Add Education
				</Button>
			)}
		</div>
	);
};

export default UserProfileEducation;
